import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { FaVideo, FaRobot, FaClock, FaEye, FaCalendarAlt, FaNewspaper } from 'react-icons/fa';

const RecentUploadsTab = () => {
  const [recentCasefiles, setRecentCasefiles] = useState([]);
  const [evidenceCounts, setEvidenceCounts] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  // Fetch recent public casefiles (last 7 days)
  useEffect(() => {
    const q = query(
      collection(db, 'casefiles'),
      where('isPublic', '==', true),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const files = [];
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        // Filter for cases created in the last 7 days
        if (data.createdAt && data.createdAt.toDate() > weekAgo) {
          files.push({ id: doc.id, ...data });
        }
      });
      setRecentCasefiles(files);
      setIsLoading(false);
    }, (error) => {
      console.error("Error fetching recent casefiles:", error);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Fetch evidence counts for recent casefiles
  useEffect(() => {
    if (recentCasefiles.length === 0) return;

    const casefileIds = recentCasefiles.map(cf => cf.id);
    // Temporary: Use simple collection query while indexes are building
    const q = query(collection(db, 'evidence'));

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const counts = {};
      
      // Initialize counts
      casefileIds.forEach(id => {
        counts[id] = {
          totalEvidence: 0,
          analyzedEvidence: 0,
          totalVehicles: 0,
          totalPlates: 0
        };
      });

      // Process evidence
      querySnapshot.forEach((doc) => {
        const evidence = { id: doc.id, ...doc.data() };
        const casefileId = evidence.casefileId;
        
        // Only process evidence for recent casefiles
        if (counts[casefileId]) {
          counts[casefileId].totalEvidence++;
          
          // Check if analyzed
          if (evidence.analysis && evidence.analysis.trim() !== '') {
            counts[casefileId].analyzedEvidence++;
            
            // Count vehicles and plates from AI analysis
            const analysisText = evidence.analysis;
            if (!analysisText.toLowerCase().includes('error:') && 
                analysisText.trim().toLowerCase() !== 'n/a') {
              
              try {
                const jsonMatch = analysisText.match(/\[.*\]|\{.*\}/s);
                if (jsonMatch) {
                  const jsonData = JSON.parse(jsonMatch[0]);
                  if (Array.isArray(jsonData)) {
                    counts[casefileId].totalVehicles += jsonData.length;
                    
                    const platesInEvidence = jsonData.filter(item => 
                      item["License Plate Number"] && 
                      item["License Plate Number"] !== 'Not detected' &&
                      item["License Plate Number"].length >= 2
                    ).length;
                    counts[casefileId].totalPlates += platesInEvidence;
                  }
                }
              } catch (e) {
                // Fallback parsing if needed
              }
            }
          }
        }
      });

      setEvidenceCounts(counts);
    });

    return () => unsubscribe();
  }, [recentCasefiles]);

  const formatTimeAgo = (date) => {
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return date.toLocaleDateString();
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="tab-content recent-uploads-tab">
        <div className="tab-header">
          <h2>Recent Uploads</h2>
          <p>Recently shared public cases</p>
        </div>
        <p>Loading recent cases...</p>
      </div>
    );
  }

  return (
    <div className="tab-content recent-uploads-tab">
      <div className="tab-header">
        <h2>Recent Uploads</h2>
        <p>Public cases shared in the last 7 days</p>
      </div>

      {recentCasefiles.length === 0 ? (
        <div className="empty-state">
          <FaNewspaper className="empty-icon" />
          <h3>No Recent Cases</h3>
          <p>No public cases have been shared in the last 7 days.</p>
          <p>Be the first to share a new case!</p>
          <Link to="/dashboard" className="cta-button">
            <FaNewspaper /> Create Public Case
          </Link>
        </div>
      ) : (
        <div className="recent-cases-grid">
          {recentCasefiles.map((casefile) => {
            const counts = evidenceCounts[casefile.id] || {
              totalEvidence: 0,
              analyzedEvidence: 0,
              totalVehicles: 0,
              totalPlates: 0
            };

            return (
              <div key={casefile.id} className="case-card recent-case">
                <div className="case-header">
                  <div className="case-title-section">
                    <Link to={`/casefile/${casefile.id}`} className="case-title">
                      {casefile.title}
                    </Link>
                    <div className="case-meta">
                      <span className="creator">
                        by {casefile.creatorEmail || 'Anonymous'}
                      </span>
                      <span className="time-ago">
                        <FaClock /> {formatTimeAgo(casefile.createdAt.toDate())}
                      </span>
                    </div>
                  </div>
                  <div className="case-date">
                    <FaCalendarAlt />
                    {formatDate(casefile.createdAt.toDate())}
                  </div>
                </div>

                <div className="case-description">
                  {casefile.description.length > 150 
                    ? `${casefile.description.substring(0, 150)}...` 
                    : casefile.description}
                </div>

                <div className="case-stats">
                  <div className="stat-item">
                    <FaVideo className="stat-icon" />
                    <span className="stat-number">{counts.totalEvidence}</span>
                    <span className="stat-label">Evidence</span>
                  </div>
                  <div className="stat-item">
                    <FaRobot className="stat-icon" />
                    <span className="stat-number">{counts.analyzedEvidence}</span>
                    <span className="stat-label">Analyzed</span>
                  </div>
                  <div className="stat-item">
                    <FaEye className="stat-icon" />
                    <span className="stat-number">{counts.totalVehicles}</span>
                    <span className="stat-label">Vehicles</span>
                  </div>
                  {counts.totalPlates > 0 && (
                    <div className="stat-item highlight">
                      <FaEye className="stat-icon" />
                      <span className="stat-number">{counts.totalPlates}</span>
                      <span className="stat-label">Plates</span>
                    </div>
                  )}
                </div>

                <div className="case-actions">
                  <Link to={`/casefile/${casefile.id}`} className="view-button">
                    <FaEye /> View Case
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="recent-footer">
        <p>Showing {recentCasefiles.length} recent case{recentCasefiles.length !== 1 ? 's' : ''}</p>
        <Link to="/public" className="view-all-button">
          View All Public Cases
        </Link>
      </div>
    </div>
  );
};

export default RecentUploadsTab; 